/**
 * WsReconnectCoordinator
 *
 * Prevents the "reconnect storm" that causes the page to freeze after a tab
 * has been hidden for a long time.
 *
 * Background:
 *   y-websocket's internal reconnect loop resets its exponential backoff on
 *   every successful WebSocket.onopen event — even if the server immediately
 *   closes the handshake (stale session, rate-limited room, etc.). When a tab
 *   is hidden, the browser aggressively throttles timers so reconnect attempts
 *   silently queue up. When the tab becomes visible again, every queued retry
 *   fires near-simultaneously, hammering the backend at ~100ms intervals and
 *   flooding the console with WebSocket errors — often locking the main thread.
 *
 * Strategy:
 *   1. Hidden > HIDDEN_PAUSE_THRESHOLD_MS → call provider.disconnect() on every
 *      registered provider that is currently connected or connecting. This
 *      tears down y-websocket's internal retry timer, guaranteeing no queued
 *      reconnects can accumulate.
 *   2. Visible again (after we paused) → after a short settle delay, call
 *      provider.connect() one at a time, staggered by RECONNECT_STAGGER_MS,
 *      so the backend never sees N simultaneous opens from a single client.
 *   3. The health watchdog (below) is the backstop for (2): if the one-shot
 *      staggered resume never fires or gets cancelled (a quick re-hide inside
 *      the settle window, a bfcache restore that computes hiddenForMs=0, or
 *      any other timing gap), a provider can be left marked `pausedByUs`
 *      forever with the tab fully visible — every other recovery path
 *      (watchdog, online/focus/interaction) used to explicitly SKIP
 *      `pausedByUs` providers, so nothing would ever un-strand it short of a
 *      full page reload. This is the exact "chandler+marketing" incident:
 *      the tab's WS layer went dark for 21+ hours while chat kept working
 *      fine over SSE. The watchdog now treats a still-paused provider with
 *      the tab visible as stranded too and force-resumes it.
 *   4. "Connected zombie" detection: `@hocuspocus/provider`'s own connection
 *      heartbeat (`checkConnection`) only fires once a message has ever been
 *      received on the socket — a socket that opened but has never gotten a
 *      single message back is reported healthy forever. The watchdog also
 *      tracks providers that read `wsconnected=true` but `synced=false` for
 *      too long, and providers stuck `wsconnecting=true` for too long, and
 *      force-reconnects them (bypassing the provider's own idempotent
 *      connect() via `forceReconnect()` where available).
 *   5. No recovery path is gated on `navigator.onLine`. Every periodic
 *      recovery path used to hard-veto whenever `navigator.onLine === false`
 *      — but that flag is a known-unreliable browser signal that can get
 *      stuck reporting `false` after a messy network transition (interface
 *      change, VPN reconnect, flaky roam) long after connectivity actually
 *      returns, with no guaranteed event to un-stick it. That let a single
 *      bad reading wedge the ENTIRE self-healing system, including the
 *      watchdog that exists specifically to be the universal backstop — the
 *      backstop can't back-stop its own failure mode if it shares the same
 *      veto. See `isRecoverySuppressed()`: recovery only ever stands down
 *      for a deliberate hidden-pause or a non-visible tab. A `connect()`
 *      attempt while genuinely offline is a cheap, self-limiting no-op.
 *
 * Registration is piggy-backed onto wsTokenRefresher.register/unregister so
 * the common case requires zero call-site changes.
 */
import type { WebsocketProvider } from 'y-websocket';
import { createFileLogger } from '../utils/logger';
import { isHardStopped } from './yjs/wsHardStop';
import { reportZombieRecovery } from './yjs/wsHealthBeacon';

const log = createFileLogger('WS_RECONNECT_COORD', { enabled: true });

// Pause providers once the tab has been hidden for this long. Short hides
// (tab switches, app switches) stay well under this threshold so normal
// backoff handles them — we only intervene when the backoff loop has had
// enough time to go pathological in a throttled background tab.
const HIDDEN_PAUSE_THRESHOLD_MS = 30_000;

// After the tab becomes visible, wait this long before starting the
// staggered resume. Gives the event loop a moment to breathe after the
// browser un-throttles and prevents racing with any in-flight teardown.
const VISIBILITY_SETTLE_MS = 500;

// Gap between each provider's resume. Prevents thundering-herd opens
// against the WS server when many rooms are active at once.
const RECONNECT_STAGGER_MS = 300;

// Connection-health watchdog cadence. While the tab is visible we periodically
// sweep for providers stranded in a disconnected state and re-issue connect().
// y-websocket only auto-reconnects while its internal `shouldConnect` flag is
// true; a socket left disconnected by a pause that never resumed, an HMR orphan,
// or a partial teardown would otherwise NEVER come back on its own. This sweep
// is the catch-all that guarantees recovery regardless of what caused the drop.
const HEALTH_CHECK_INTERVAL_MS = 5_000;

// A provider reporting `wsconnected=true` but `synced=false` for this long
// (while visible + online) is treated as a "connected zombie" — the socket
// opened but the server's sync handshake never completed and never will on
// its own (see the module doc). Comfortably above normal handshake latency
// (sub-second) even on a slow connection.
const ZOMBIE_UNSYNCED_MS = 25_000;

// A provider reporting `wsconnecting=true` continuously for this long is
// stuck retrying (e.g. the library's internal retry state got wedged).
// Comfortably above the provider's own capped backoff ceiling (30s).
const STUCK_CONNECTING_MS = 60_000;

/** True only for providers that expose the `forceReconnect()` escape hatch
 * (currently just `HocuspocusWebsocketProvider`). Plain y-websocket
 * providers don't have it — zombie recovery is a no-op (logged) for those. */
function hasForceReconnect(
  provider: WebsocketProvider,
): provider is WebsocketProvider & { forceReconnect: () => void } {
  return typeof (provider as unknown as { forceReconnect?: unknown }).forceReconnect === 'function';
}

class WsReconnectCoordinator {
  private readonly providers = new Set<WebsocketProvider>();
  private readonly pausedByUs = new WeakSet<WebsocketProvider>();
  // Per-provider predicates that, when truthy at pause time, keep that provider
  // connected through a long hide. Used by conversation rooms with an in-flight
  // agent run: tearing the socket down mid-run drops live streaming events
  // (the server keeps pushing into a room with clients=0), so the run only
  // becomes visible again on a full reload.
  private readonly exemptionChecks = new Map<WebsocketProvider, () => boolean>();
  private hiddenSince: number | null = null;
  private hiddenTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly resumeTimers = new Set<ReturnType<typeof setTimeout>>();
  // Separate from resumeTimers so the visibility-resume scheduling guard
  // (`resumeTimers.size === 0`) is never confused by an in-flight health sweep.
  private readonly healthTimers = new Set<ReturnType<typeof setTimeout>>();
  private readonly longHideHandlers = new Set<() => void>();
  // First-observed timestamps for the zombie sweep. WeakMaps so a provider
  // that's unregistered (or GC'd) drops its tracking automatically.
  private readonly unsyncedSince = new WeakMap<WebsocketProvider, number>();
  private readonly connectingSince = new WeakMap<WebsocketProvider, number>();

  constructor() {
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => this.onVisibilityChange());
    }
    if (typeof window !== 'undefined') {
      // Safari/Firefox bfcache restore: visibilitychange may not fire reliably,
      // so pageshow.persisted is the canonical signal to treat as "visible again".
      window.addEventListener('pageshow', (e) => {
        if ((e as PageTransitionEvent).persisted) {
          this.handleVisible();
        }
      });
      // Network restored — short-circuit y-websocket's exponential backoff and
      // reconnect any stranded provider immediately instead of waiting for the
      // next backoff tick (up to 30s) or the next watchdog sweep.
      window.addEventListener('online', () => this.reconnectStrandedProviders());
      // Window focus — kick stranded providers immediately (e.g. OS-level window
      // switch that didn't fire visibilitychange on all browsers).
      window.addEventListener('focus', () => this.reconnectStrandedProviders());
      // Throttled interaction gate: reconnect any stranded provider as soon as
      // the user starts typing or clicking so they never wait up to the full
      // watchdog interval for a room to come back.
      let _lastInteractionCheck = 0;
      const _onInteraction = () => {
        const now = Date.now();
        if (now - _lastInteractionCheck < 2_000) return;
        _lastInteractionCheck = now;
        this.reconnectStrandedProviders();
      };
      window.addEventListener('pointerdown', _onInteraction, { passive: true, capture: true });
      window.addEventListener('keydown', _onInteraction, { passive: true, capture: true });
    }
    // Periodic connection-health watchdog. Created once at module load under
    // real timers; the body is also exposed publicly so tests can drive it
    // deterministically without depending on this interval firing.
    if (typeof setInterval !== 'undefined') {
      const handle = setInterval(() => {
        this.reconnectStrandedProviders();
        this.detectAndRecoverZombies();
      }, HEALTH_CHECK_INTERVAL_MS);
      // Never keep a Node process (tests / SSR) alive on account of the watchdog.
      (handle as unknown as { unref?: () => void })?.unref?.();
    }
  }

  register(provider: WebsocketProvider): void {
    this.providers.add(provider);
  }

  unregister(provider: WebsocketProvider): void {
    this.providers.delete(provider);
    this.pausedByUs.delete(provider);
    this.exemptionChecks.delete(provider);
    this.unsyncedSince.delete(provider);
    this.connectingSince.delete(provider);
  }

  /**
   * Register a predicate that exempts a provider from the long-hide pause while
   * it returns true. Re-evaluated on every pause, so a provider is only kept
   * alive for as long as the condition holds (e.g. an active agent run). Pass
   * the same provider again to replace the check; call clearPauseExemption to
   * remove it.
   */
  setPauseExemption(provider: WebsocketProvider, check: () => boolean): void {
    this.exemptionChecks.set(provider, check);
  }

  clearPauseExemption(provider: WebsocketProvider): void {
    this.exemptionChecks.delete(provider);
  }

  /**
   * Register a handler to call when the tab has been hidden long enough to trigger
   * a provider pause (i.e. when HIDDEN_PAUSE_THRESHOLD_MS is exceeded). Use this
   * to tear down expensive ephemeral resources (e.g. conversation Y.Docs) that
   * should not keep their in-memory state across a long background interval.
   * Returns an unsubscribe function.
   */
  onLongHide(handler: () => void): () => void {
    this.longHideHandlers.add(handler);
    return () => this.longHideHandlers.delete(handler);
  }

  private onVisibilityChange(): void {
    if (typeof document === 'undefined') return;
    if (document.visibilityState === 'hidden') {
      this.handleHidden();
    } else {
      this.handleVisible();
    }
  }

  private handleHidden(): void {
    if (this.hiddenTimer !== null) return;
    // Cancel any in-flight resume/health reconnect — we're hidden again, so
    // completing a staggered reconnect would open WS connections in a
    // now-throttled tab (the storm pauseAll() exists to prevent).
    this.clearResumeTimers();
    this.clearHealthTimers();
    this.hiddenSince = Date.now();
    this.hiddenTimer = setTimeout(() => {
      this.hiddenTimer = null;
      this.pauseAll();
    }, HIDDEN_PAUSE_THRESHOLD_MS);
  }

  private handleVisible(): void {
    if (this.hiddenTimer !== null) {
      clearTimeout(this.hiddenTimer);
      this.hiddenTimer = null;
    }
    const hiddenForMs = this.hiddenSince !== null ? Date.now() - this.hiddenSince : 0;
    this.hiddenSince = null;

    // Re-assert every connected provider's awareness state immediately on
    // every visibility restore — not just the long-hide (>=30s) pause case
    // below. Browsers throttle setInterval in a backgrounded tab well before
    // that threshold (Chrome can clamp a background tab's timers to 1/min
    // after as little as a few minutes hidden), which can silently push
    // y-protocols' own ~15s awareness self-refresh past both the server's
    // 30s idle timeout and the client's own 30s receive watchdog — dropping
    // a connection that would have stayed healthy in a foregrounded tab.
    // Kicking awareness here re-arms both watchdogs the instant the tab
    // wakes, instead of waiting for the (possibly still-throttled) interior
    // timer to catch up.
    this.kickAwareness();

    // Immediately reconnect any stranded providers regardless of how long the
    // tab was hidden. This covers providers that were NOT explicitly paused by
    // us (e.g. network blip, IDB-delayed connect) on every tab return.
    //
    // Deliberately excludes pausedByUs providers here (includeStrandedPaused=
    // false): resumeAllStaggered() below already owns their recovery on this
    // exact transition, via the settle delay this method's callers rely on to
    // avoid a thundering herd of simultaneous opens right as the tab wakes.
    // The strandedPaused backstop (for when THAT scheduled resume itself gets
    // missed/cancelled — the D1 strand) instead runs from the periodic
    // watchdog interval and the online/focus/interaction triggers below,
    // which are decoupled from this timing.
    this.reconnectStrandedProviders(false);

    // Only schedule a staggered resume if we actually paused AND no resume is
    // already in flight. Without the second guard, a spurious duplicate
    // 'visible' event would cancel an in-flight resume without scheduling a
    // replacement — leaving providers disconnected.
    if (hiddenForMs >= HIDDEN_PAUSE_THRESHOLD_MS && this.resumeTimers.size === 0) {
      const settle = setTimeout(() => {
        this.resumeTimers.delete(settle);
        this.resumeAllStaggered();
      }, VISIBILITY_SETTLE_MS);
      this.resumeTimers.add(settle);
    }
  }

  private pauseAll(): void {
    let paused = 0;
    let exempt = 0;
    // Snapshot the set — a misbehaving provider's disconnect() could
    // synchronously trigger an unregister() via status listeners, and
    // iterating a mutating Set can skip entries.
    for (const provider of Array.from(this.providers)) {
      try {
        const connected = Boolean((provider as { wsconnected?: boolean }).wsconnected);
        const connecting = Boolean((provider as { wsconnecting?: boolean }).wsconnecting);
        if (!connected && !connecting) continue;
        // Keep exempt providers (e.g. conversation rooms with an in-flight run)
        // connected so live streaming events aren't lost to a clients=0 room.
        const check = this.exemptionChecks.get(provider);
        if (check) {
          let isExempt = false;
          try { isExempt = check(); } catch (err) { log.warn('Exemption check threw:', err); }
          if (isExempt) { exempt += 1; continue; }
        }
        provider.disconnect();
        this.pausedByUs.add(provider);
        paused += 1;
      } catch (err) {
        log.warn('Error pausing provider:', err);
      }
    }
    if (paused > 0 || exempt > 0) {
      log.log(`Tab hidden >${HIDDEN_PAUSE_THRESHOLD_MS}ms — paused ${paused} WS provider(s)${exempt > 0 ? `, kept ${exempt} exempt (active run)` : ''}`);
    }

    // Notify long-hide handlers so callers can tear down ephemeral resources
    // (e.g. conversation Y.Docs) whose bloated in-memory state would cause
    // expensive resync payloads on the next reconnect.
    for (const handler of this.longHideHandlers) {
      try { handler(); } catch (err) {
        log.warn('Error in long-hide handler:', err);
      }
    }
  }

  private resumeAllStaggered(): void {
    const toResume: WebsocketProvider[] = [];
    for (const provider of this.providers) {
      if (this.pausedByUs.has(provider)) {
        toResume.push(provider);
      }
    }
    if (toResume.length === 0) return;

    log.log(
      `Tab visible — resuming ${toResume.length} paused WS provider(s) (stagger=${RECONNECT_STAGGER_MS}ms)`
    );

    toResume.forEach((provider, idx) => {
      const timer = setTimeout(() => {
        this.resumeTimers.delete(timer);
        if (!this.providers.has(provider)) return;
        if (!this.pausedByUs.has(provider)) return;
        try {
          provider.connect();
          this.pausedByUs.delete(provider);
        } catch (err) {
          log.warn('Error resuming provider:', err);
        }
      }, idx * RECONNECT_STAGGER_MS);
      this.resumeTimers.add(timer);
    });
  }

  private clearResumeTimers(): void {
    for (const t of this.resumeTimers) clearTimeout(t);
    this.resumeTimers.clear();
  }

  private clearHealthTimers(): void {
    for (const t of this.healthTimers) clearTimeout(t);
    this.healthTimers.clear();
  }

  private isLive(provider: WebsocketProvider): boolean {
    const connected = Boolean((provider as { wsconnected?: boolean }).wsconnected);
    const connecting = Boolean((provider as { wsconnecting?: boolean }).wsconnecting);
    return connected || connecting;
  }

  /**
   * Immediately re-broadcast awareness local state for every connected
   * provider. y-protocols' own `Awareness` self-refresh (re-sending local
   * state every ~15s — see `awareness.js`'s `_checkInterval`) is what keeps
   * a presence-free room (e.g. `org:`) inside both the server's 30s idle
   * timeout and the client's own 30s receive watchdog. That timer is a
   * plain `setInterval`, so a throttled background tab can silently starve
   * it — this is the explicit "kick" that beats the throttling by firing
   * the moment we know the tab is live again, rather than trusting the
   * interior timer to have kept up while hidden.
   *
   * Re-setting state to the SAME value is deliberate and sufficient: y-js
   * Awareness emits an `update` (and therefore a wire message) on every
   * `setLocalState()` call regardless of whether the value actually
   * changed — see y-protocols/awareness.js. A no-op for providers with no
   * awareness or a null local state (a room that isn't participating in
   * awareness-based presence at all, or one mid-teardown).
   */
  private kickAwareness(): void {
    for (const provider of this.providers) {
      try {
        if (!this.isLive(provider)) continue;
        const awareness = (
          provider as unknown as { awareness?: { getLocalState(): unknown; setLocalState(state: unknown): void } }
        ).awareness;
        if (!awareness) continue;
        const state = awareness.getLocalState();
        if (state === null || state === undefined) continue;
        awareness.setLocalState(state);
      } catch (err) {
        log.warn('kickAwareness: error refreshing provider awareness:', err);
      }
    }
  }

  /**
   * Returns true only when the provider itself wants to be connected.
   *
   * y-websocket maintains a `shouldConnect` flag that is:
   *   - `true` on construction and after every `connect()` call
   *   - `false` after every `disconnect()` call
   *
   * Involuntary drops (network blip, server close) only set `wsconnected=false`
   * via `closeWebsocketConnection` — they NEVER touch `shouldConnect`. So
   * `shouldConnect === true` is exactly "y-websocket wants to be connected but
   * currently isn't" (the only case where forcing `connect()` is correct).
   *
   * Any deliberate teardown — our own `pauseAll()`, hard-stop, collab disconnect
   * button, corruption handler — goes through `disconnect()` which sets
   * `shouldConnect=false`. Gating on this flag means we never fight intentional
   * states regardless of which code path caused the disconnect.
   */
  private wantsConnection(provider: WebsocketProvider): boolean {
    return Boolean((provider as { shouldConnect?: boolean }).shouldConnect);
  }

  /**
   * The only states in which automatic recovery must stand down: mid-hide /
   * pending-pause (don't undo a deliberate pause-in-progress) and a
   * non-visible tab (don't open sockets in a throttled background tab).
   *
   * `navigator.onLine` is deliberately NOT consulted here. It's a known-
   * unreliable browser signal — after a messy network transition (interface
   * change, VPN reconnect, flaky WiFi roam) it can get stuck reporting
   * `false` long after connectivity is actually restored, and the only thing
   * that would normally flip it back is a browser `online` event that can
   * itself be missed. Every recovery path used to hard-veto on this flag,
   * which meant a single stuck `false` reading could wedge the ENTIRE
   * self-healing system — including the periodic watchdog that exists
   * specifically to be the backstop for exactly this kind of edge case —
   * with no way to recover short of a full page reload. Dropping the veto
   * is safe: attempting `connect()` while genuinely offline is a cheap,
   * self-limiting no-op (the provider's own idempotence guard plus its
   * capped backoff mean at most one wasted dial per stranded provider per
   * backoff cycle), while trusting a stale flag risks permanent WS death.
   */
  private isRecoverySuppressed(): boolean {
    if (this.hiddenSince !== null || this.hiddenTimer !== null) return true;
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return true;
    return false;
  }

  /**
   * Immediately connect a single provider if it is stranded (wants a
   * connection but is currently down, not paused by us, and not hard-stopped).
   *
   * Use this at user-action call sites (e.g. doc mount) instead of waiting up
   * to HEALTH_CHECK_INTERVAL_MS for the periodic watchdog to fire.
   */
  ensureConnected(provider: WebsocketProvider): void {
    if (this.isRecoverySuppressed()) return;
    if (this.pausedByUs.has(provider)) return;
    if (!this.wantsConnection(provider)) return;
    try { if (isHardStopped(provider)) return; } catch { return; }
    if (this.isLive(provider)) return;
    try {
      provider.connect();
    } catch (err) {
      log.warn('ensureConnected: error connecting provider:', err);
    }
  }

  /**
   * Immediately reconnect ALL stranded providers without waiting for the next
   * watchdog interval. Safe to call at high frequency — each call inspects the
   * registered set but fires connect() only for genuinely stranded providers.
   *
   * Equivalent to reconnectStrandedProviders() but bypasses the stagger delay
   * so recovery is instantaneous at the call site (visibility return, send, etc.).
   */
  ensureAllConnected(): void {
    this.reconnectStrandedProviders();
  }

  /**
   * Connection-health watchdog body — the universal "always reconnect" guarantee.
   *
   * Re-issues `connect()` on any registered provider that is disconnected AND
   * still wants to be connected (`shouldConnect === true`). When a provider is
   * genuinely stranded — e.g. y-websocket's backoff is stuck, the network was
   * briefly lost, or an HMR orphan slipped past the globalThis guards — this
   * sweep re-arms `setupWS` within at most one watchdog interval.
   *
   * Driven by the periodic interval AND the `window 'online'` event. Public so
   * tests can invoke it deterministically.
   *
   * Guards (each prevents fighting an intentional state):
   *   - Tab must be visible and not pending-pause — never reconnect in a
   *     throttled background tab, and never undo the deliberate hidden-pause.
   *   - `wantsConnection(provider)` — the definitive gate: any `disconnect()`
   *     (our pause, hard-stop, collab disconnect, corruption handler) sets
   *     `shouldConnect=false`; we must not override that.
   *   - `pausedByUs` is kept as an explicit, fast-path skip so it is visible
   *     in the logic even though it implies `shouldConnect=false`.
   *   - `isHardStopped` is kept for the same reason, and because hard-stop
   *     also shadows `connect()` on the provider instance, so calling it would
   *     spam the gated-connect warning every tick.
   *   - Reconnects are staggered to avoid a thundering herd after a mass outage.
   *
   * @param includeStrandedPaused Whether to also force-resume providers still
   *   marked `pausedByUs` (the D1-strand backstop). Defaults to true for the
   *   periodic watchdog and the online/focus/interaction triggers. Pass false
   *   from a call site that already owns a paused provider's recovery on this
   *   exact tick via its own deliberately-delayed path (see handleVisible()).
   */
  reconnectStrandedProviders(includeStrandedPaused: boolean = true): void {
    if (this.isRecoverySuppressed()) return;

    const stranded: WebsocketProvider[] = [];
    // Providers still marked `pausedByUs` even though the tab is visible and
    // we are not mid-hide/mid-pause: the one-shot resume in handleVisible()
    // never fired or was cancelled. Their own `shouldConnect` flag is FALSE
    // (we set that ourselves in pauseAll()), so `wantsConnection()` would
    // normally exclude them — that's the backstop's whole point, so it's
    // deliberately checked separately below instead of gating on that flag.
    // Callers that already own a paused provider's recovery on this exact
    // tick (handleVisible()'s own settle+stagger resume) pass
    // includeStrandedPaused=false to avoid double-resuming it outside that
    // deliberate delay.
    const strandedPaused: WebsocketProvider[] = [];
    for (const provider of Array.from(this.providers)) {
      try {
        if (isHardStopped(provider)) continue;
      } catch (err) {
        log.warn('isHardStopped check threw — skipping provider:', err);
        continue;
      }
      if (this.isLive(provider)) continue;
      if (this.pausedByUs.has(provider)) {
        if (includeStrandedPaused) strandedPaused.push(provider);
        continue;
      }
      if (!this.wantsConnection(provider)) continue;
      stranded.push(provider);
    }
    if (stranded.length === 0 && strandedPaused.length === 0) return;

    if (stranded.length > 0) {
      log.log(
        `Health check: reconnecting ${stranded.length} stranded WS provider(s) (stagger=${RECONNECT_STAGGER_MS}ms)`
      );
    }
    if (strandedPaused.length > 0) {
      log.warn(
        `Health check: ${strandedPaused.length} provider(s) still marked paused with the tab visible — ` +
        `the resume-on-visible path missed them, force-resuming now (D1 strand backstop)`
      );
    }

    [...stranded, ...strandedPaused].forEach((provider, idx) => {
      const timer = setTimeout(() => {
        this.healthTimers.delete(timer);
        // Re-validate at fire time — state may have changed during the stagger.
        if (!this.providers.has(provider)) return;
        if (this.isLive(provider)) return;
        const wasPaused = this.pausedByUs.has(provider);
        if (!wasPaused && !this.wantsConnection(provider)) return;
        try {
          if (isHardStopped(provider)) return;
          if (wasPaused) this.pausedByUs.delete(provider);
          provider.connect();
        } catch (err) {
          log.warn('Error reconnecting stranded provider:', err);
        }
      }, idx * RECONNECT_STAGGER_MS);
      this.healthTimers.add(timer);
    });
  }

  /**
   * Detect and recover providers wedged in a state none of the other
   * recovery paths can see: reporting "connected" or "connecting" per their
   * own status while the underlying socket is actually dead or mute. See the
   * module doc (point 4) for why this can't self-heal without help.
   *
   * Runs on the same cadence as `reconnectStrandedProviders()` but is a
   * strictly additive check — it does not affect that method's stranded-set
   * logic and is skipped for providers already handled there (paused,
   * hard-stopped, or genuinely disconnected-and-stranded).
   */
  detectAndRecoverZombies(): void {
    if (this.isRecoverySuppressed()) return;

    const now = Date.now();
    for (const provider of Array.from(this.providers)) {
      if (this.pausedByUs.has(provider)) {
        this.unsyncedSince.delete(provider);
        this.connectingSince.delete(provider);
        continue;
      }
      try {
        if (isHardStopped(provider)) {
          this.unsyncedSince.delete(provider);
          this.connectingSince.delete(provider);
          continue;
        }
      } catch (err) {
        log.warn('isHardStopped check threw during zombie sweep — skipping provider:', err);
        continue;
      }

      const connected = Boolean((provider as { wsconnected?: boolean }).wsconnected);
      const connecting = Boolean((provider as { wsconnecting?: boolean }).wsconnecting);
      const synced = Boolean((provider as { synced?: boolean }).synced);

      if (connected && !synced) {
        const since = this.unsyncedSince.get(provider);
        if (since === undefined) {
          this.unsyncedSince.set(provider, now);
        } else if (now - since >= ZOMBIE_UNSYNCED_MS) {
          this.unsyncedSince.delete(provider);
          this.connectingSince.delete(provider);
          this.recoverZombie(provider, 'connected_never_synced');
          continue;
        }
      } else {
        this.unsyncedSince.delete(provider);
      }

      if (connecting) {
        const since = this.connectingSince.get(provider);
        if (since === undefined) {
          this.connectingSince.set(provider, now);
        } else if (now - since >= STUCK_CONNECTING_MS) {
          this.connectingSince.delete(provider);
          this.recoverZombie(provider, 'stuck_connecting');
        }
      } else {
        this.connectingSince.delete(provider);
      }
    }
  }

  private recoverZombie(
    provider: WebsocketProvider,
    reason: 'connected_never_synced' | 'stuck_connecting',
  ): void {
    if (!hasForceReconnect(provider)) {
      log.warn(`Zombie WS provider detected (reason=${reason}) but forceReconnect() is unavailable — skipping`);
      return;
    }
    log.warn(`Zombie WS provider detected (reason=${reason}) — force-reconnecting`);
    try {
      provider.forceReconnect();
      const roomName = (provider as unknown as { roomname?: string }).roomname ?? '?';
      reportZombieRecovery(roomName.split(':')[0], reason);
    } catch (err) {
      log.warn('forceReconnect failed:', err);
    }
  }

  /** Debug helper — snapshot of current coordinator state. */
  public getDebugState(): {
    registeredCount: number;
    exemptionCount: number;
    hiddenSince: number | null;
    hiddenForMs: number;
    pendingPause: boolean;
    pendingResumes: number;
    pendingHealthReconnects: number;
  } {
    return {
      registeredCount: this.providers.size,
      exemptionCount: this.exemptionChecks.size,
      hiddenSince: this.hiddenSince,
      hiddenForMs: this.hiddenSince !== null ? Date.now() - this.hiddenSince : 0,
      pendingPause: this.hiddenTimer !== null,
      pendingResumes: this.resumeTimers.size,
      pendingHealthReconnects: this.healthTimers.size,
    };
  }
}

// Preserve the singleton across Vite HMR module re-executions (same pattern as
// wsClientSingleton and SystemContext). Without this, editing any file in the
// coordinator's import chain re-runs the constructor, adding duplicate event
// listeners and a second watchdog interval — while the preserved wsClient still
// holds providers registered to the old coordinator instance that the new one
// can no longer see (split-brain). One stable instance for the lifetime of the
// page is the correct invariant.
const _COORD_KEY = Symbol.for('__river_ws_reconnect_coordinator__');
const _coordGlobal = globalThis as unknown as { [_COORD_KEY]?: WsReconnectCoordinator };
export const wsReconnectCoordinator: WsReconnectCoordinator =
  _coordGlobal[_COORD_KEY] ?? (_coordGlobal[_COORD_KEY] = new WsReconnectCoordinator());
